import { Button, useDisclosure, useToast } from '@chakra-ui/react';
import { MdAutoAwesome } from 'react-icons/md';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCreateChatbot } from '@/lib/sdk/chatbot/queries';
import type { ChatbotFormData, WorkflowLinks } from '@/lib/sdk/chatbot/types';
import type { AIDraftExport } from '@/lib/sdk/ai-chat/types';
import { useState } from 'react';
import { PageContainer } from '@/client/components/layout/PageContainer';
import { ChatbotFormV4 } from '../components/ChatbotFormV4';
import { AIBuildPanel } from '../ai/AIBuildPanel';
import { formDataFromExport } from '../ai/draftExport';
import { useCanBuildWithAI } from '../ai/useCanBuildWithAI';
import type { BuildBriefState } from '@/modules/guide/components/SolvePanel';

/** A draft from the AI assistant loaded into the form; key remounts the form */
interface LoadedDraft {
  key: number;
  data: ChatbotFormData;
  links?: WorkflowLinks;
}

export const CreateChatbotV3 = () => {
  const toast = useToast();
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const createChatbotMutation = useCreateChatbot();
  const canBuildWithAI = useCanBuildWithAI();
  const [searchParams] = useSearchParams();
  // Build this on a Solve plan arrives with the workflow's brief to send to Build
  const aiBrief = (useLocation().state as BuildBriefState | null)?.aiBrief;
  // ?ai=1 arrives with the panel open (the old AI page redirects here)
  const aiPanel = useDisclosure({ defaultIsOpen: searchParams.get('ai') === '1' });
  const [loadedDraft, setLoadedDraft] = useState<LoadedDraft | null>(null);

  const openDraft = (draftExport: AIDraftExport) => {
    if (!draftExport.create) return;
    setLoadedDraft({ key: Date.now(), data: formDataFromExport(draftExport.create), links: draftExport.create.links });
    toast({ title: 'Draft opened in the editor', description: 'Review it, then save.', status: 'success', duration: 4000 });
  };

  const breadcrumbItems = [
    { label: 'Dashboard', href: `/dashboard/${orgId}/workflows` },
    { label: 'Create Workflow' },
  ];

  const handleSubmit = async (data: ChatbotFormData) => {
    try {
      setIsLoading(true);
      const hasLinks = data.pendingLinks && Object.keys(data.pendingLinks).length > 0;
      await createChatbotMutation.mutateAsync({
        mode: 'ui',
        data: {
          title: data.title,
          template: data.template,
          tasks: data.tasks,
          ttl: data.ttl ?? 1,
          tags: data.tags,
          task_lifecycle: data.task_lifecycle,
          ttl_unit: data.ttl_unit,
          // Links persist atomically with the chatbot in the create call, so a
          // STARTNEW task can never be saved without its linked workflows.
          ...(hasLinks ? { links: data.pendingLinks } : {}),
        },
      });

      toast({
        title: hasLinks ? 'Workflow created with linked workflows' : 'Workflow created successfully',
        status: 'success',
        duration: 3000,
      });

      navigate(`/dashboard/${orgId}/workflows`);
    } catch (error: any) {
      toast({
        title: 'Error creating workflow',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageContainer
      title="Workflows"
      description="Create a new workflow configuration."
      breadcrumbs={breadcrumbItems}
      action={
        canBuildWithAI ? (
          <Button size="sm" leftIcon={<MdAutoAwesome />} onClick={aiPanel.onOpen} data-testid="wfv4-ai-btn">
            Build with AI
          </Button>
        ) : undefined
      }
    >
      <ChatbotFormV4
        key={loadedDraft?.key ?? 'blank'}
        onSubmit={handleSubmit}
        isLoading={isLoading}
        orgId={orgId}
        initialData={loadedDraft?.data}
        initialLinks={loadedDraft?.links}
      />
      {canBuildWithAI && (
        <AIBuildPanel isOpen={aiPanel.isOpen} onClose={aiPanel.onClose} onOpenInEditor={openDraft} initialMessage={aiBrief} />
      )}
    </PageContainer>
  );
};

export default CreateChatbotV3;
